import type { CarrierSurchargePolicy, CarrierSurchargeTable } from '@plt/refund-quote'
import { callApi } from '../api'

/** The YQ/YR table the Refunds screen quotes with, and the screen that changes it (refund-quote SPEC.md §3). */

export interface CarrierSurchargeChange {
  id: number
  carrier: string
  /** Null when this change removed the carrier. */
  policy: CarrierSurchargePolicy | null
  reason: string
  changedBy: string
  changedAt: string
}

export interface CarrierSurchargeEvidence {
  carrier: string
  refunded: number
  withheld: number
  unclear: number
  lastSeenAt: string
}

export interface CarrierSurchargeState {
  live: CarrierSurchargeChange[]
  history: CarrierSurchargeChange[]
  /** Null when the evidence could not be read; the table still works without it. */
  evidence: CarrierSurchargeEvidence[] | null
  evidenceDays: number
  you: string | null
}

export const getCarrierSurcharges = () =>
  callApi<CarrierSurchargeState>('/bookings/refund-surcharges', {}, 'Could not load the YQ/YR table')

export const getLiveCarrierSurcharges = () =>
  callApi<{ carriers: CarrierSurchargeTable }>(
    '/bookings/refund-surcharges/live',
    {},
    'Could not load the live YQ/YR table',
  )

export const setCarrierSurcharge = (carrier: string, policy: CarrierSurchargePolicy | null, reason: string) =>
  callApi<CarrierSurchargeChange>(
    '/bookings/refund-surcharges',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ carrier, policy, reason }),
    },
    'Could not save the change',
  )

/** Enough quotes, nearly all one way. */
const MIN_TICKETS = 5
const MIN_SHARE = 0.9

/** What the evidence says this carrier does, or null when it is too thin or too mixed to say. */
export function suggestedPolicy(evidence: CarrierSurchargeEvidence): CarrierSurchargePolicy | null {
  const total = evidence.refunded + evidence.withheld + evidence.unclear
  if (total < MIN_TICKETS) return null
  if (evidence.refunded / total >= MIN_SHARE) return 'refunds'
  if (evidence.withheld / total >= MIN_SHARE) return 'withholds'
  return null
}
